import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';
import { useNavigate, useLocation } from 'react-router-dom';
import { LuSearch } from 'react-icons/lu';
import { MdEmail } from 'react-icons/md';
import { PiBookBookmarkFill } from 'react-icons/pi';
import { FaRegClock} from 'react-icons/fa';
import { useNoobookContext } from '../context/NoobookContext';
import BookTimerPage from '../pages/BookTimerPage';
import { FaTimes } from 'react-icons/fa';
import BookSearchPage from '../pages/BookSearchPage';
import BookRegisterPage from '../pages/BookRegisterPage';
import MembershipSubscriptionPage from '../pages/MembershipSubscriptionPage';
import { useUserInfoQuery, useUnreadMessageCountQuery} from '../react-queries/queries';
import { noobookRole2text } from '../common/enums';
import { useLogoutMutation } from '../react-queries/mutations';
import { NoobookRole } from '../common/enums';
import { HELPER_ID } from '../config';


const NavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 18px 16px 8px 16px;
  background: #eae8e9;
  position: relative;
  z-index: 101;
  box-shadow: none;
`;
const Logo = styled.div`
  font-size: 24px;
  font-weight: bold;
`;
const IconGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
`;
const IconBox = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s;
  &:active {
    background: #ededed;
  }
`;

const BellIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UnreadCountBadge = styled.span`
  position: absolute;
  top: -5px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #e74c3c;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  box-sizing: border-box;
  pointer-events: none;
`;
const iconStyle = { width: 24, height: 24, color: '#888' };


const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  height: 100vh;
  background: #fff;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fff;
`;

const ModalTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #222;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f5f5f7;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 0px 0px 0px;
`;

// 드롭다운 메뉴 스타일
const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const MenuDrawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 320px;
  height: 100vh;
  background: #fff;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const MenuHeader = styled.div`
  padding: 20px 16px;
  border-bottom: 1px solid #eee;
  background: #fff;
`;

const UserInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 16px;
  border-bottom: 1px solid #eee;
`;

const UserImage = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #eae8e9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #222;
`;

const UserRole = styled.div`
  font-size: 14px;
  color: #666;
`;

const MenuList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

const MenuItem = styled.div`
  padding: 0;
`;

const MenuItemButton = styled.button`
  width: 100%;
  padding: 16px 20px;
  background: none;
  border: none;
  text-align: left;
  font-size: 16px;
  color: #222;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:active {
    background: #eee;
  }
`;

const SubMenuList = styled.div`
  background: #f9f9f9;
  overflow: hidden;
  max-height: ${props => props.isOpen ? '500px' : '0'};
  transition: max-height 0.3s ease-out;
`;

const SubMenuItem = styled.button`
  width: 100%;
  padding: 12px 20px 12px 40px;
  background: none;
  border: none;
  text-align: left;
  font-size: 15px;
  color: #444;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f0f0f0;
  }
  
  &:active {
    background: #e8e8e8;
  }
`;




const BookTimerModal = ({ timerType, timerBookId, onClose }) => {
  return (
    <Modal>
      <ModalHeader>
        <ModalTitle>{timerType == 'read' ? '독서' : '필사'} 타이머</ModalTitle>
        <CloseButton onClick={()=>{onClose();}}>
          <FaTimes />
        </CloseButton>
      </ModalHeader>
      <ModalContent>
        <BookTimerPage propPageTimerType={timerType}  propBookId={timerBookId} onClose={onClose} />
      </ModalContent>
    </Modal>
  )
};

const BookSearchModal = ({ onClose }) => {
  return (
    <Modal>
      <ModalHeader>
        <ModalTitle>책 검색</ModalTitle>
        <CloseButton onClick={()=>{onClose(null, null);}}>
          <FaTimes />
        </CloseButton>
      </ModalHeader>
      <ModalContent>
        <BookSearchPage onClose={onClose} />
      </ModalContent>
    </Modal>
  )
};

const BookRegisterModal = ({ isbn, bookId, onClose }) => {
  return (
    <Modal>
      <ModalHeader>
        <ModalTitle>책 등록</ModalTitle>
        <CloseButton onClick={()=>{ if(window.confirm('등록 중인 내용이 저장되지 않습니다. 책 등록을 취소하시겠습니까?')) onClose(false);}}>
          <FaTimes />
        </CloseButton>
      </ModalHeader>
      <ModalContent>
        <BookRegisterPage propIsbn={isbn} propBookId={bookId} onClose={onClose} />
      </ModalContent>
    </Modal>
  )
};

const MembershipModal = ({ onClose }) => {
  return (
    <Modal>
      <ModalHeader>
        <ModalTitle>멤버십 구독</ModalTitle>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
      </ModalHeader>
      <ModalContent>
        <MembershipSubscriptionPage onClose={onClose} />
      </ModalContent>
    </Modal>
  );
};

const DropDownMenu = ({ userInfo, menuItems, closeMenu }) => {
   const [openSubMenus, setOpenSubMenus] = useState({});
   // 서브메뉴 토글
   const toggleSubMenu = (menuKey) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

// 메뉴 아이템 클릭 핸들러
  const handleMenuClick = (menu, subMenu = null) => {
    if (subMenu) {
      if(subMenu.onClick) {
        subMenu.onClick();
      }
      // 2depth 메뉴 클릭
      //closeMenu();
      // 여기에 실제 메뉴 동작 추가
      console.log(`Menu clicked: ${menu.key} > ${subMenu.key}`);
    } else {
      
      // 1depth 메뉴 클릭
      //const menuConfig = menuItems.find(item => item.key === menuKey);
      //if (menuConfig && menuConfig.subMenus && menuConfig.subMenus.length > 0) {
      if(menu.subMenus && menu.subMenus.length > 0) {
      // 서브메뉴가 있으면 토글
        toggleSubMenu(menu.key);
      } else {
        if(menu.onClick) {
          menu.onClick();
        }
        // 서브메뉴가 없으면 메뉴 닫고 동작
        //closeMenu();
        // 여기에 실제 메뉴 동작 추가
        console.log(`Menu clicked: ${menu.key}`);
      }
    }
  };
 

  
  return (
    <>
        <MenuOverlay onClick={closeMenu} />
        <MenuDrawer>
          <MenuHeader>
            <CloseButton onClick={closeMenu}>
              <FaTimes />
            </CloseButton>
          </MenuHeader>
          <UserInfoSection>
            <UserImage>
              {userInfo?.imgFileUrl ? (
                <img src={userInfo.imgFileUrl} alt="User" />
              ) : (
                <div style={{ fontSize: '24px', color: '#999' }}>👤</div>
              )}
            </UserImage>
            <UserName>{userInfo?.name || '사용자'}</UserName>
            <UserRole>{userInfo?.userRole ? noobookRole2text(userInfo.userRole) : ''}</UserRole>
          </UserInfoSection>
          <MenuList>
            {menuItems.filter(item => !item?.hidden??false).map((item) => (
              <MenuItem key={item.key}>
                <MenuItemButton onClick={() => handleMenuClick(item)}>
                  <span>{item.label}</span>
                  {item.subMenus && item.subMenus.length > 0 && (
                    <ChevronRightIcon 
                      style={{ 
                        width: 20, 
                        height: 20, 
                        color: '#666',
                        transform: openSubMenus[item.key] ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }} 
                    />
                  )}
                </MenuItemButton>
                {item.subMenus && item.subMenus.length > 0 && (
                  <SubMenuList isOpen={openSubMenus[item.key]}>
                    {item.subMenus.map((subMenu) => (
                      <SubMenuItem 
                        key={subMenu.key}
                        onClick={() => handleMenuClick(item, subMenu)}
                      >
                        {subMenu.label}
                      </SubMenuItem>
                    ))}
                  </SubMenuList>
                )}
              </MenuItem>
            ))}
          </MenuList>
        </MenuDrawer>
      </>
  )
};

/**
 * @param {{showBack?: boolean, onBack?: () => void, logoText?: string}} props
 */
const TopNav = ({ showBack = false, onBack, logoText = '누북'}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {timerStartAt, timerEndAt, timerBookId, timerType, onBookRegistered } = useNoobookContext();
  const [isBookTimerOpen, setIsBookTimerOpen] = useState(false);
  const [isBookSearchOpen, setIsBookSearchOpen] = useState(false);
  const [isBookRegisterOpen, setIsBookRegisterOpen] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [regIsbn, setRegIsbn] = useState(null);
  const [regBookId, setRegBookId] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { mutate: logout, isPending } = useLogoutMutation();
 

  const handleLogout = () => {
    if (timerBookId && timerType && timerStartAt && timerEndAt && timerStartAt < timerEndAt) {
      const confirmed = window.confirm('북 타이머가 동작 중입니다. 로그아웃전에 타이머를 중단하고 기록을 저장하시겠습니까?');
      if (confirmed) {
        closeMenu();
        popupBookTimer();
        return;
      } else {
        return;
      }
    }



    if (window.confirm('로그아웃 하시겠습니까?')) {
      logout(undefined, {
        onSuccess: () => {
          //refetchUserInfo();
          navigate('/login');
        },
        onError: (error) => {
          alert('로그아웃 실패: ' + error.message);
        }
      });
    }
  };
  
  //const menuDrawerRef = useRef(null);
  //const menuIconRef = useRef(null);
  const { data: userInfo, refetch: refetchUserInfo } = useUserInfoQuery();
  const { data: unreadMessageCount = 0 } = useUnreadMessageCountQuery();
  // 현재 페이지와 이동하려는 페이지가 같으면 이동하지 않는 함수들
  const navigateToBookTimer = () => {
    const targetPath = `/book-timer/${timerType}/${timerBookId}`;
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  const popupBookTimer = () => {
    const targetPath = `/book-timer/${timerType}/${timerBookId}`;
    if (location.pathname === targetPath) {
      return;
    }
    if (!timerType || !timerBookId) {
      return;
    }
    setIsBookTimerOpen(true);
  };

  const navigateToBookSearch = () => {
    const targetPath = '/book-search';
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  const popupBookSearch = () => {
    const targetPath = '/book-search';
    if (location.pathname === targetPath) {
      return;
    }
    setIsBookSearchOpen(true);
  };

  const handleBookSearchClose = (argIsbn, argBookId) => {
    setIsBookSearchOpen(false);
    if (!argIsbn && !argBookId) {
      return;
    }
    const result = window.confirm(`선택한 도서를 등록(수정)하시겠습니까?`);
    if (!result) {
       return;
    } 
    setRegIsbn(argIsbn);
    setRegBookId(argBookId);
    setIsBookRegisterOpen(true);
  };

  const handleBookRegisterClose = (modified) => {
    setIsBookRegisterOpen(false);
    if(modified) {
     // onBookRegistered();
     if(onBookRegistered) {
      //alert('onBookRegistered');
      onBookRegistered();
     }
     
    }
  };

  // 메뉴 토글
  

  // 메뉴 구조 정의
  const menuItems = [
    { key: 'my-reading', label: '나의 독서(누독)', subMenus: [], onClick: ()=>{navigate('/nudok');closeMenu(); } },
    { key: 'my-script', label: '나의필사(누필)', subMenus: [], onClick: ()=>{navigate('/nupil');closeMenu(); } },
    { 
      key: 'moim', 
      label: '모임', 
      subMenus: [
        { key: 'my-moim', label: '운영중인 모임', onClick: ()=>{navigate('/my-info/meetings', { replace: true, state: { forceRefresh: Date.now() } });closeMenu();} }
      ],
      hidden: (userInfo?.userRole != NoobookRole.LEADER && userInfo?.userRole != NoobookRole.PROFESSIONAL && userInfo?.userRole != NoobookRole.ADMIN)
    },
    { 
      key: 'content', 
      label: '누북콘텐츠', 
      subMenus: [
        { key: 'youtube', label: '누북유튜브', onClick: ()=>{} },
        { key: 'event', label: '기획콘텐츠, 이벤트', onClick: ()=>{} }
      ]
    },
    { 
      key: 'premium', 
      label: '프리미엄 멤버십', 
      subMenus: [
        { key: 'premium-info', label: '멤버십 안내 및 혜택', onClick: ()=>{ setShowMembershipModal(true); closeMenu(); } }
      ]
    },
    { 
      key: 'banner', 
      label: '배너광고/제휴', 
      subMenus: [
        {
          key: 'banner-register',
          label: '배너 등록/문의',
          onClick: () => {
            navigate('/messages/write', {
              state: { recipientLoginId: HELPER_ID, title: '배너 등록/문의' },
            });
            closeMenu();
          },
        },
        { key: 'partnership', 
          label: '파트너십 신청', 
          onClick: ()=>{
            navigate('/messages/write', {
              state: { recipientLoginId: HELPER_ID, title: '파트너십 신청' },
            });
            closeMenu();
          } 
        }
      ]
    },
    { 
      key: 'account', 
      label: '계정', 
      subMenus: [
        { key: 'edit-info', label: '회원정보 수정', onClick: ()=>{navigate('/settings');closeMenu(); } },
        { key: 'logout', label: '로그아웃', onClick: handleLogout },
        { key: 'withdraw', label: '회원탈퇴', onClick: ()=>{} }
      ]
    },
    { 
      key: 'service', 
      label: '서비스 안내', 
      subMenus: [
        { key: 'terms', label: '이용약관', onClick: ()=>{navigate('/terms/service');closeMenu(); } },
        { key: 'privacy', label: '개인정보처리방침', onClick: ()=>{navigate('/terms/privacy');closeMenu(); } },
        { key: 'customer', label: '고객센터', onClick: ()=>{navigate('/customer-center');closeMenu(); } },
        { key: 'version', label: '버전정보', onClick: ()=>{} }
      ]
    }
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      //setOpenSubMenus({});
    }
  };

  // 메뉴 닫기
  const closeMenu = () => {
    setIsMenuOpen(false);
    //setOpenSubMenus({});
  };

  

  // 뒤로가기 버튼으로 모달 닫기
  useEffect(() => {
    const handlePopState = (event) => {
      if (isMenuOpen) {
        event.preventDefault();
        closeMenu();
      } else if (isBookTimerOpen) {
        event.preventDefault();
        setIsBookTimerOpen(false);
        // history를 다시 push하여 뒤로가기 이벤트를 취소
        //window.history.pushState(null, '', window.location.href);
      } else if (isBookSearchOpen) {
        event.preventDefault();
        handleBookSearchClose(null, null);
        // history를 다시 push하여 뒤로가기 이벤트를 취소
        //window.history.pushState(null, '', window.location.href);
      } else if (isBookRegisterOpen) {
        event.preventDefault();
        handleBookRegisterClose(false);
        // history를 다시 push하여 뒤로가기 이벤트를 취소
        //window.history.pushState(null, '', window.location.href);
      } else if (showMembershipModal) {
        event.preventDefault();
        setShowMembershipModal(false);
      }
    };

    // 모달이 열려있을 때만 history state를 push하고 이벤트 리스너 등록
    if (isMenuOpen || isBookTimerOpen || isBookSearchOpen || isBookRegisterOpen || showMembershipModal) {
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isMenuOpen, isBookTimerOpen, isBookSearchOpen, isBookRegisterOpen, showMembershipModal]);

  const navigateToMyLibrary = () => {
    const targetPath = '/my-library';
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  const navigateToMessages = () => {
    const targetPath = '/messages';
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  return (
    <>
    <NavBar>
      {/*showBack ? (
        <IconBox onClick={onBack || (() => navigate(-1))}>
          <ChevronLeftIcon style={iconStyle} />
        </IconBox>
      ) : (
        <Logo>{logoText}</Logo>
      )*/}
      {
        showBack && !location.pathname.startsWith('/my-info') && (
          <IconBox onClick={onBack || (() => navigate(-1))}>
            <ChevronLeftIcon style={iconStyle} />
          </IconBox>
        )
      }
      <Logo>{logoText}</Logo>
      <IconGroup>
        {timerStartAt && timerEndAt && timerBookId && timerType && (
          <IconBox onClick={popupBookTimer}>
            <FaRegClock size={22} color={iconStyle.color} />
          </IconBox>
        )}
        <IconBox onClick={popupBookSearch}>
          <LuSearch size={22} color={iconStyle.color} />
        </IconBox>
        <IconBox onClick={navigateToMyLibrary}>
          <PiBookBookmarkFill size={22} color={iconStyle.color} />
        </IconBox>
        <IconBox onClick={navigateToMessages}>
          <BellIconWrapper>
            <MdEmail size={22} color={iconStyle.color} />
            {unreadMessageCount > 0 && (
              <UnreadCountBadge>
                {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
              </UnreadCountBadge>
            )}
          </BellIconWrapper>
        </IconBox>
        <IconBox  onClick={toggleMenu}>
          <Bars3Icon style={iconStyle} />
        </IconBox>
      </IconGroup>
    </NavBar>
    {isBookTimerOpen && (
      <BookTimerModal timerType={timerType} timerBookId={timerBookId} onClose={()=>setIsBookTimerOpen(false)} />
    )}
    {isBookSearchOpen && (
      <BookSearchModal onClose={handleBookSearchClose} />
    )}
    {isBookRegisterOpen && (
      <BookRegisterModal isbn={regIsbn} bookId={regBookId} onClose={handleBookRegisterClose} />
    )}
    {showMembershipModal && (
      <MembershipModal onClose={() => setShowMembershipModal(false)} />
    )}
    {isMenuOpen && (
      <DropDownMenu userInfo={userInfo} menuItems={menuItems} closeMenu={closeMenu} />
    )}
    </>
  );
};

export default TopNav; 